import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { ArrowLeft, Sparkle, ClipboardText, ImageSquare } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * InspectionCompare — side-by-side Move-In vs Move-Out photo comparison
 * so damage is obvious at review time. Route: /admin/property-inspections/:id/compare
 */
export default function InspectionCompare() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  useEffect(() => {
    axios.get(`${BASE}/api/inspections/${id}/compare`, auth())
      .then(r => setData(r.data))
      .catch(() => toast.error("Couldn't load comparison"));
    axios.get(`${BASE}/api/inspections/${id}/compare/ai`, auth()).then(r => { if (r.data?.rooms) setAi(r.data); }).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  const runAi = async () => {
    setAiBusy(true);
    toast("Claude is comparing move-in vs move-out photos room by room — 30–90s", { duration: 4000 });
    try { const r = await axios.post(`${BASE}/api/inspections/${id}/compare/ai`, { max_photos_per_side: 4 }, auth()); setAi(r.data); toast.success("AI comparison ready"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI comparison failed")); }
    finally { setAiBusy(false); }
  };
  const VERDICT = { new_damage: "bg-red-100 text-red-800 border-red-400", wear_only: "bg-amber-100 text-amber-800 border-amber-400",
    no_change: "bg-emerald-100 text-emerald-800 border-emerald-400", unclear: "bg-slate-100 text-slate-700 border-slate-300" };

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="inspection-compare-page">
        <div className="flex items-center gap-3">
          <Link to={`/admin/property-inspections/${id}`} className="w-9 h-9 border-2 border-slate-300 hover:bg-slate-100 flex items-center justify-center">
            <ArrowLeft weight="bold" size={16}/>
          </Link>
          <div className="flex-1">
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight inline-flex items-center gap-2">
              <Sparkle weight="fill" size={28} className="text-fuchsia-500"/> Compare
            </h1>
            <div className="text-sm text-slate-600 font-medium">
              Room-matched photos, current inspection vs paired inspection at the same address.
            </div>
          </div>
        </div>

        {!data && <div className="mt-6 text-slate-400 text-sm">Loading…</div>}
        {data?.partner && (
          <div className="mt-4 p-4 bg-[#0b1c2d] text-white rounded-2xl" data-testid="ai-compare-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-black text-lg">AI Move-In vs Move-Out</div>
                <div className="text-xs text-slate-300">Claude Vision compares each room's photos and separates new damage from normal wear.</div>
              </div>
              <button onClick={runAi} disabled={aiBusy} data-testid="ai-compare-run"
                className="h-11 px-4 rounded-xl bg-teal-500 disabled:opacity-50 text-slate-900 font-black text-sm">
                {aiBusy ? "Comparing…" : ai ? "Re-run AI compare" : "Run AI compare"}
              </button>
            </div>
            {ai?.rooms && (
              <div className="mt-3 space-y-2" data-testid="ai-compare-results">
                <div className="text-xs text-slate-300 font-bold">{ai.totals?.rooms_compared} rooms compared · <span className="text-red-300">{ai.totals?.rooms_with_new_damage} with new damage</span> · {ai.totals?.chargeable_items} chargeable items</div>
                {ai.rooms.map((r, k) => (
                  <div key={k} className="p-3 rounded-xl bg-white/5 border border-white/10 text-sm" data-testid={`ai-room-${k}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-black">{r.room}</span>
                      {r.skipped ? <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-slate-600">skipped · {r.reason}</span>
                        : <span className={`px-2 py-0.5 rounded-full text-[10px] font-black border ${VERDICT[r.verdict] || VERDICT.unclear}`}>{(r.verdict || "").replace("_", " ")}</span>}
                      <span className="text-[10px] text-slate-400">{r.move_in_photos} in · {r.move_out_photos} out</span>
                    </div>
                    {r.summary && <div className="mt-1 text-slate-200">{r.summary}</div>}
                    {(r.new_damage || []).map((d, j) => (
                      <div key={j} className="mt-1.5 pl-3 border-l-2 border-red-500 text-xs">
                        <b className="text-red-300">{d.what}</b> — {d.where} · {d.severity}{d.chargeable ? <span className="ml-1 px-1.5 rounded bg-red-600 text-white font-black">CHARGEABLE</span> : null}{d.est_repair ? <span className="text-slate-400"> · {d.est_repair}</span> : null}
                      </div>
                    ))}
                    {(r.pre_existing || []).length > 0 && <div className="mt-1 text-xs text-slate-400"><b>Pre-existing:</b> {r.pre_existing.join("; ")}</div>}
                    {(r.normal_wear || []).length > 0 && <div className="mt-0.5 text-xs text-slate-400"><b>Normal wear:</b> {r.normal_wear.join("; ")}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {data && (
          <>
            <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
              <div className="p-3 border-2 border-violet-300 bg-violet-50">
                <div className="text-[9px] font-black uppercase tracking-widest text-violet-700">Current</div>
                <div className="font-black">{(data.current.kind || "").replace("_", "-").toUpperCase()}</div>
                <div className="text-[11px] text-slate-500">{fmt(data.current.created_at)}</div>
              </div>
              <div className={`p-3 border-2 ${data.partner ? "border-cyan-300 bg-cyan-50" : "border-slate-300 bg-slate-50"}`}>
                <div className="text-[9px] font-black uppercase tracking-widest text-cyan-700">Paired</div>
                {data.partner ? (
                  <>
                    <div className="font-black">{(data.partner.kind || "").replace("_", "-").toUpperCase()}</div>
                    <div className="text-[11px] text-slate-500">{fmt(data.partner.created_at)}</div>
                  </>
                ) : (
                  <div className="text-slate-500 italic">No paired inspection found at this address yet.</div>
                )}
              </div>
            </div>

            <div className="mt-6 space-y-6">
              {data.pairs.length === 0 && (
                <div className="p-10 text-center border-2 border-dashed border-slate-200 text-slate-400 text-sm">
                  <ClipboardText size={40} className="mx-auto text-slate-300"/>
                  <div className="mt-3">No rooms to compare yet.</div>
                </div>
              )}
              {data.pairs.map(row => (
                <div key={row.room} className="border-2 border-slate-200 bg-white" data-testid={`compare-row-${row.room}`}>
                  <div className="p-3 border-b border-slate-100 flex items-center gap-2">
                    <div className="font-black text-slate-900">{row.room}</div>
                    <div className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {row.current.length} · {row.partner.length}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 divide-x-2 divide-slate-100">
                    <PhotoStrip photos={row.current} label="CURRENT"/>
                    <PhotoStrip photos={row.partner} label="PAIRED"/>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
}

function PhotoStrip({ photos, label }) {
  if (!photos.length) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[110px] text-slate-400 text-xs bg-slate-50/60">
        <div className="text-center">
          <ImageSquare size={22} className="mx-auto text-slate-300"/>
          <div className="mt-1 text-[10px] font-black uppercase tracking-widest">No {label.toLowerCase()} photos</div>
        </div>
      </div>
    );
  }
  return (
    <div className="p-2 grid grid-cols-3 gap-1">
      {photos.slice(0, 9).map((p, k) => (
        <div key={k} className="aspect-square border border-slate-200 overflow-hidden bg-slate-100">
          <img src={`${BASE}/api/inspections/walk-photo/${p.path}?auth=${encodeURIComponent(localStorage.getItem("fr_token") || localStorage.getItem("token") || "")}`}
            alt="" className="w-full h-full object-cover"/>
        </div>
      ))}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
